import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import ResiduosSelector from "@/components/puntolimpio/ResiduosSelector";
import { adminCrearCheckinTelefono, getCatalogoResiduos } from "@/api";
import { getErrorMessage } from "@/lib/errors";
import { validateDniNie, validateCif } from "@/lib/dni";

const emptyForm = {
  tipo_solicitante: "ciudadano",
  nombre_apellidos: "", dni_nie: "", razon_social: "", cif: "",
  direccion: "", email: "", apuntado: true, acepta_clausula: false,
};

export default function PuntoLimpioTelefonoDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [residuos, setResiduos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const residuosRef = useRef(null);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const isEmpresa = form.tipo_solicitante === "empresa";

  useEffect(() => {
    if (open && catalogo.length === 0) {
      getCatalogoResiduos().then(setCatalogo).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setForm(emptyForm);
    setResiduos([]);
    onClose();
  };

  const canSubmit =
    (isEmpresa
      ? form.razon_social.trim().length > 1 && validateCif(form.cif || "")
      : form.nombre_apellidos.trim().length > 1 && validateDniNie(form.dni_nie || "")) &&
    form.direccion.trim().length > 2 && form.acepta_clausula && residuos.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (residuos.length === 0) {
      toast.error("Añade al menos un residuo que el vecino va a entregar");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        tipo_solicitante: form.tipo_solicitante,
        nombre_apellidos: isEmpresa ? null : form.nombre_apellidos,
        dni_nie: isEmpresa ? null : form.dni_nie,
        razon_social: isEmpresa ? form.razon_social : null,
        cif: isEmpresa ? form.cif : null,
        direccion: form.direccion,
        email: form.email.trim() || null,
        deseaemail: false,
        apuntado: isEmpresa ? false : form.apuntado,
        acepta_clausula: form.acepta_clausula,
        residuos,
      };
      await adminCrearCheckinTelefono(payload);
      toast.success("Visita al Punto Limpio registrada como pendiente");
      onCreated?.();
      handleClose();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo registrar la visita"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="checkin-telefono-dialog">
        <DialogHeader>
          <DialogTitle className="text-municipal font-heading flex items-center gap-2">
            <Phone className="w-5 h-5" /> Registrar entrega telefónica al Punto Limpio
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-xs text-gray-400">
            Úsalo cuando un vecino avisa por teléfono de que va a traer (o ya ha traído) residuos al
            Punto Limpio Móvil. Queda como check-in pendiente, igual que si lo hubiera rellenado él
            mismo en la web; luego se confirma normalmente desde "Check-ins pendientes".
          </p>

          <RadioGroup value={form.tipo_solicitante} onValueChange={(v) => update({ tipo_solicitante: v })} className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2 border rounded-lg px-3 py-2">
              <RadioGroupItem value="ciudadano" id="pl-tel-ciudadano" />
              <Label htmlFor="pl-tel-ciudadano" className="font-normal cursor-pointer">Ciudadano</Label>
            </div>
            <div className="flex items-center space-x-2 border rounded-lg px-3 py-2">
              <RadioGroupItem value="empresa" id="pl-tel-empresa" />
              <Label htmlFor="pl-tel-empresa" className="font-normal cursor-pointer">Empresa</Label>
            </div>
          </RadioGroup>

          {isEmpresa ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Razón social</Label><Input value={form.razon_social} onChange={(e) => update({ razon_social: e.target.value })} data-testid="pl-tel-razon-social-input" /></div>
              <div><Label className="text-xs mb-1 block">CIF</Label><Input value={form.cif} onChange={(e) => update({ cif: e.target.value.toUpperCase() })} data-testid="pl-tel-cif-input" /></div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Nombre y apellidos</Label><Input value={form.nombre_apellidos} onChange={(e) => update({ nombre_apellidos: e.target.value })} data-testid="pl-tel-nombre-input" /></div>
              <div><Label className="text-xs mb-1 block">DNI/NIE</Label><Input value={form.dni_nie} onChange={(e) => update({ dni_nie: e.target.value.toUpperCase() })} data-testid="pl-tel-dni-input" /></div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1 block">Dirección</Label><Input value={form.direccion} onChange={(e) => update({ direccion: e.target.value })} data-testid="pl-tel-direccion-input" /></div>
            <div><Label className="text-xs mb-1 block">Correo electrónico (opcional)</Label><Input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} data-testid="pl-tel-email-input" /></div>
          </div>

          <div>
            <Label className="text-xs mb-1.5 block">Residuos que entrega</Label>
            <ResiduosSelector ref={residuosRef} catalogo={catalogo} value={residuos} onChange={setResiduos} />
          </div>

          {!isEmpresa && (
            <div className="flex items-center gap-2">
              <Checkbox id="pl-tel-apuntado" checked={form.apuntado} onCheckedChange={(v) => update({ apuntado: v })} data-testid="pl-tel-apuntado-checkbox" />
              <Label htmlFor="pl-tel-apuntado" className="font-normal text-sm cursor-pointer">El vecino quiere apuntarse a la bonificación de la tasa de basuras</Label>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox id="pl-tel-clausula" checked={form.acepta_clausula} onCheckedChange={(v) => update({ acepta_clausula: v })} data-testid="pl-tel-clausula-checkbox" />
            <Label htmlFor="pl-tel-clausula" className="font-normal text-sm cursor-pointer">El ciudadano ha dado su consentimiento para el tratamiento de sus datos</Label>
          </div>

          <Button type="submit" disabled={!canSubmit || submitting} className="w-full bg-municipal hover:bg-municipal-dark text-white" data-testid="pl-tel-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Registrar entrega telefónica
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
